/**
 * Walk-Away Range Cell Renderer
 *
 * Shows the bid ceilings derived from the lot's estimates and fees:
 *   - "Stay under": highest bid where the LOW estimate still clears the
 *     user's target margin (even a pessimistic flip works below this)
 *   - "Max": bid where the HIGH estimate breaks even (never exceed)
 * plus a bid-position bar plotting the next required bid against the range
 * (green = safe, yellow = judgment zone, red = past the ceiling).
 *
 * Unlike the meat badge, these numbers do not depend on the current bid, so
 * they stay valid between estimate/fee changes — the number to carry into the
 * final minutes of an auction.
 *
 * CSP note: the portal forbids inline style attributes, so the marker is
 * positioned via quantized data-pos attribute selectors (5% steps) defined in
 * tableCells.module.css, and the zone segments have fixed proportions
 * (55% safe / 30% judgment / 15% over). The bar communicates zone + depth
 * within the zone rather than a strictly linear dollar scale.
 */

import type { Lot, PortalSettings } from '../../types';
import { parseCurrencyValue, formatCurrencyDisplay, escapeAttr } from '../formatting';
import { calculateWalkAwayRange, classifyLotBidPosition } from '../lotOperations';
import styles from './tableCells.module.css';

/** Fixed zone proportions (percent of bar width) — must match the CSS module. */
const SAFE_ZONE_END = 55;
const JUDGMENT_ZONE_END = 85;

function formatBidValue(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) return '—';
  return formatCurrencyDisplay(amount) ?? '—';
}

/** Quantize a 0–100 percent to 5%-step buckets matching the CSS rules. */
function quantizePos(pct: number): number {
  return Math.max(0, Math.min(100, Math.round(pct / 5) * 5));
}

const POSITION_LABEL: Record<string, string> = {
  safe: 'Below your stay-under ceiling',
  judgment: 'In your judgment zone',
  over: 'Past your walk-away ceiling',
};

/**
 * Map the next bid to a bar position. Each zone interpolates independently:
 *   safe:      0 → stayUnder            maps onto [0, SAFE_ZONE_END]
 *   judgment:  stayUnder → neverExceed  maps onto (SAFE_ZONE_END, JUDGMENT_ZONE_END]
 *   over:      neverExceed → 2×ceiling  maps onto (JUDGMENT_ZONE_END, 100]
 */
function markerPosition(
  nextBid: number,
  stayUnder: number | null,
  neverExceed: number | null,
  position: 'safe' | 'judgment' | 'over'
): number {
  const lerp = (frac: number, from: number, to: number) =>
    from + Math.max(0, Math.min(1, frac)) * (to - from);

  if (position === 'safe' && stayUnder != null && stayUnder > 0) {
    return lerp(nextBid / stayUnder, 0, SAFE_ZONE_END);
  }
  if (position === 'judgment') {
    const lo = stayUnder ?? 0;
    const hi = neverExceed ?? lo;
    if (hi > lo) {
      return lerp((nextBid - lo) / (hi - lo), SAFE_ZONE_END, JUDGMENT_ZONE_END);
    }
    return (SAFE_ZONE_END + JUDGMENT_ZONE_END) / 2;
  }
  if (position === 'over') {
    const ceiling = neverExceed ?? stayUnder;
    if (ceiling != null && ceiling > 0) {
      return lerp((nextBid - ceiling) / ceiling, JUDGMENT_ZONE_END, 100);
    }
    return 100;
  }
  return 0;
}

export function buildWalkAwayCell(lot: Lot, settings: PortalSettings | null | undefined): string {
  const range = calculateWalkAwayRange(lot, settings);
  if (!range || (range.stayUnderBid == null && range.neverExceedBid == null)) {
    return '<span class="' + styles.walkAwayEmpty + '">—</span>';
  }

  const hasManualMax = typeof lot.userMaxBid === 'number' && Number.isFinite(lot.userMaxBid);
  const position = classifyLotBidPosition(lot, settings);

  // ── Range lines ──────────────────────────────────────────────────────
  const line = (label: string, value: string, extraClass = '') =>
    '<div class="' +
    styles.walkAwayLine +
    (extraClass ? ' ' + extraClass : '') +
    '"><span class="' +
    styles.walkAwayLabel +
    '">' +
    label +
    '</span> <span class="' +
    styles.walkAwayValue +
    '">' +
    value +
    '</span></div>';

  const stayUnderLine =
    range.stayUnderBid != null ? line('Stay under', formatBidValue(range.stayUnderBid)) : '';
  const neverExceedLine =
    range.neverExceedBid != null ? line('Max', formatBidValue(range.neverExceedBid)) : '';
  const manualLine = hasManualMax
    ? line('My max', formatBidValue(lot.userMaxBid as number), styles.walkAwayManual)
    : '';

  // ── Bid-position bar ─────────────────────────────────────────────────
  let positionBar = '';
  const nextBid = parseCurrencyValue(
    lot.nextRequiredBid != null ? lot.nextRequiredBid : lot.nextRequiredBidRaw
  );
  if (nextBid != null && position) {
    const pos = quantizePos(
      markerPosition(nextBid, range.stayUnderBid, range.neverExceedBid, position)
    );
    const markerClass =
      position === 'safe'
        ? styles.walkAwayMarkerSafe
        : position === 'judgment'
          ? styles.walkAwayMarkerJudgment
          : styles.walkAwayMarkerOver;
    const label = POSITION_LABEL[position] || '';

    positionBar =
      '<div class="' +
      styles.walkAwayBar +
      '" role="img" aria-label="' +
      escapeAttr(label) +
      '" title="' +
      escapeAttr(label) +
      '">' +
      '<div class="' +
      styles.walkAwaySegmentSafe +
      '"></div>' +
      '<div class="' +
      styles.walkAwaySegmentJudgment +
      '"></div>' +
      '<div class="' +
      styles.walkAwaySegmentOver +
      '"></div>' +
      '<div class="' +
      styles.walkAwayMarker +
      ' ' +
      markerClass +
      '" data-pos="' +
      pos +
      '"></div>' +
      '</div>';
  }

  const tooltip =
    'Stay under: low estimate still clears your ' +
    String(range.targetMarginPct) +
    '% target margin. Max: high estimate breaks even.';

  return (
    '<div class="' +
    styles.walkAwayCell +
    '" title="' +
    escapeAttr(tooltip) +
    '">' +
    stayUnderLine +
    neverExceedLine +
    manualLine +
    positionBar +
    '</div>'
  );
}
